import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import HomePage from "@/pages/HomePage";
import ServicesPage from "@/pages/ServicesPage";
import GalleryPage from "@/pages/GalleryPage";
import AboutPage from "@/pages/AboutPage";
import TestimonialsPage from "@/pages/TestimonialsPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import FAQPage from "@/pages/FAQPage";
import ContactPage from "@/pages/ContactPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import { Toaster } from "@/components/ui/sonner";
import { Menu, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Scroll to top component
function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return null;
}

function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  if (isAdminRoute) return null;

  const leftNavLinks = [
    { path: "/", label: "Home" },
    { path: "/services", label: "Services" },
    { path: "/gallery", label: "Gallery" },
    { path: "/about", label: "About" },
  ];

  const rightNavLinks = [
    { path: "/testimonials", label: "Testimonials" },
    { path: "/blog", label: "Blog" },
    { path: "/faq", label: "FAQ" },
    { path: "/contact", label: "Contact" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/98 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Desktop Navigation - Extra height for logo */}
          <div className="hidden lg:flex items-center justify-between relative" style={{ height: '120px', paddingTop: '75px' }}>
            {/* Left Nav Items */}
            <div className="flex space-x-8">
              {leftNavLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                  className={`text-sm font-medium transition-colors hover:text-[#8B7355] ${
                    location.pathname === link.path ? "text-[#8B7355]" : "text-gray-800"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Center Logo with Full Circle Background - Fully visible between header and banner */}
            <Link 
              to="/" 
              className="absolute left-1/2 transform -translate-x-1/2 z-20" 
              data-testid="logo-link"
              style={{
                top: '10px' // Position from top to ensure full visibility
              }}
            >
              <div 
                className="bg-white rounded-full flex items-center justify-center border-4 border-[#8B7355]"
                style={{
                  width: '150px',
                  height: '150px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_bartender-hub-3/artifacts/u92q6gyd_Melange%20Logo%20New%20PS.png" 
                  alt="Mélange Mobile Bar" 
                  className="w-32 h-auto"
                />
              </div>
            </Link>

            {/* Right Nav Items */}
            <div className="flex space-x-8">
              {rightNavLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                  className={`text-sm font-medium transition-colors hover:text-[#8B7355] ${
                    location.pathname === link.path ? "text-[#8B7355]" : "text-gray-800"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

        {/* Mobile/Tablet Navigation */}
        <div className="lg:hidden flex justify-center items-center h-20 relative">
          <Link to="/" className="flex items-center" data-testid="logo-link-mobile">
            <img 
              src="https://customer-assets.emergentagent.com/job_bartender-hub-3/artifacts/u92q6gyd_Melange%20Logo%20New%20PS.png" 
              alt="Mélange Mobile Bar" 
              className="h-16 w-auto"
            />
          </Link>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="absolute right-4 p-2 text-gray-800"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="lg:hidden pb-4" data-testid="mobile-menu">
            {[...leftNavLinks, ...rightNavLinks].map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                onClick={() => setIsMenuOpen(false)}
                className={`block py-2 text-sm font-medium transition-colors hover:text-[#8B7355] ${
                  location.pathname === link.path ? "text-[#8B7355]" : "text-gray-800"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
}

function Footer({ contactEmail, contactPhone }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  if (isAdminRoute) return null;

  return (
    <footer className="bg-gray-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_bartender-hub-3/artifacts/jjg0m534_Melange%20Logo%20New.png" 
                alt="Mélange Mobile Bar" 
                className="h-12 w-auto mr-3"
              />
            </div>
            <p className="text-gray-400 text-sm">
              Professional mobile bartending services for your special events.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Quick Links</h4>
            <div className="space-y-2">
              <Link to="/services" className="block text-gray-400 hover:text-[#C8A882] text-sm transition-colors">
                Services
              </Link>
              <Link to="/gallery" className="block text-gray-400 hover:text-[#C8A882] text-sm transition-colors">
                Gallery
              </Link>
              <Link to="/blog" className="block text-gray-400 hover:text-[#C8A882] text-sm transition-colors">
                Blog
              </Link>
              <Link to="/contact" className="block text-gray-400 hover:text-[#C8A882] text-sm transition-colors">
                Contact
              </Link>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Connect</h4>
            <div className="space-y-2 text-sm">
              <p className="text-gray-400">{contactEmail || "melangemobilebar@gmail.com"}</p>
              <p className="text-gray-400">{contactPhone || "+1 (443) 631-1459"}</p>
            </div>
          </div>
        </div>
        <div className="mt-8 pt-8 border-t border-gray-800 text-center text-gray-400 text-sm">
          <p>&copy; 2025 Mélange Mobile Bar. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}

function App() {
  const [settings, setSettings] = useState({
    contact_email: "",
    contact_phone: "",
    seo_title: "",
    seo_description: "",
    seo_keywords: "",
    seo_og_image: ""
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/seo`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="App min-h-screen flex flex-col">
        <Navigation />
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<HomePage seoSettings={settings} />} />
            <Route path="/services" element={<ServicesPage seoSettings={settings} />} />
            <Route path="/gallery" element={<GalleryPage seoSettings={settings} />} />
            <Route path="/about" element={<AboutPage seoSettings={settings} />} />
            <Route path="/testimonials" element={<TestimonialsPage seoSettings={settings} />} />
            <Route path="/blog" element={<BlogPage seoSettings={settings} />} />
            <Route path="/blog/:id" element={<BlogPostPage seoSettings={settings} />} />
            <Route path="/faq" element={<FAQPage seoSettings={settings} />} />
            <Route path="/contact" element={<ContactPage seoSettings={settings} />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/*" element={<AdminDashboard />} />
          </Routes>
        </main>
        <Footer 
          contactEmail={settings.contact_email} 
          contactPhone={settings.contact_phone} 
        />
        <Toaster />
      </div>
    </BrowserRouter>
  );
}

export default App;
